import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { userManager } from "@core/service-container";
import { UserUpdateData } from "@domain/user/user";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { NetworkError } from "@ui/components/common/error";
import { TextInput } from "@ui/components/inputs/text-input";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { ToastContext } from "@ui/components/common/toast-provider";
import { FormField } from "@ui/pages/prospect/components/form-field";
import { useRegistrationCountryUserConfig } from "@ui/pages/register/steps/hooks/use-registration-country-user-config";
import { sendAnalyticsEventAccountUpdated } from "@ui/pages/settings/settings-analytics";
import { row, stack } from "@ui/style";
import { isNotNil } from "@utils/assert";
import { motion } from "framer-motion";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useContext } from "react";
import { Controller, useForm } from "react-hook-form";

export interface EditProfessionalInformationDialogProps extends DialogProps {
	defaultValues: UserUpdateData;
}

export const EditProfessionalInformationDialog = ({
	defaultValues,
	...otherProps
}: EditProfessionalInformationDialogProps): JSX.Element => {
	const { format } = useI18n();
	const { showToast } = useContext(ToastContext);
	const { control, handleSubmit } = useForm<UserUpdateData>({ defaultValues });

	const {
		selectedPostalCodeTemplate,
		isLoading: isConfigLoading,
		isError: isConfigError,
		refresh: refreshConfig,
	} = useRegistrationCountryUserConfig("", () => null);

	const fieldRequiredErrorMessage = format("global.form.field.requiredError");

	const [submitForm, isLoading] = useAsyncOperation(
		useCallback(
			async (data: UserUpdateData) => {
				const updatedData: Partial<UserUpdateData> = Object.fromEntries(
					Object.entries(data).filter(([key, value]) => defaultValues[key as keyof UserUpdateData] !== value),
				);
				try {
					await userManager.updateUser(updatedData);
					sendAnalyticsEventAccountUpdated(Object.keys(updatedData).join(";"));
					showToast("", format("settings.accountTab.editInfoDialog.successToast.detail"), "success");
					otherProps.onHide();
				} catch (e) {
					console.error("Failure to submit form", e);
				}
			},
			[defaultValues, format, otherProps, showToast],
		),
		{},
	);

	let dialogContent: JSX.Element;
	if (isConfigLoading) {
		dialogContent = <LoadingContainer />;
	} else if (isConfigError) {
		dialogContent = <StyledNetworkError onRefresh={refreshConfig} />;
	} else {
		dialogContent = (
			<Form onSubmit={handleSubmit(submitForm)}>
				<ControllersContainer>
					<Controller
						name="firstName"
						control={control}
						rules={{
							required: fieldRequiredErrorMessage,
						}}
						render={({ field, fieldState }) => (
							<FormField
								title={format("settings.accountTab.professionalInformationSection.firstName")}
								errorMessage={fieldState.error?.message}
							>
								<TextInput
									{...field}
									placeholder={format("settings.accountTab.professionalInformationSection.firstName")}
									error={fieldState.invalid}
									inputMode="text"
									autoCorrect="off"
									autoCapitalize="none"
								/>
							</FormField>
						)}
					/>
					<Controller
						name="lastName"
						control={control}
						rules={{
							required: fieldRequiredErrorMessage,
						}}
						render={({ field, fieldState }) => (
							<FormField
								title={format("settings.accountTab.professionalInformationSection.lastName")}
								errorMessage={fieldState.error?.message}
							>
								<TextInput
									{...field}
									placeholder={format("settings.accountTab.professionalInformationSection.lastName")}
									error={fieldState.invalid}
									inputMode="text"
									autoCorrect="off"
									autoCapitalize="none"
								/>
							</FormField>
						)}
					/>

					<Controller
						name="postalCode"
						control={control}
						rules={{
							required: { value: isNotNil(defaultValues.postalCode), message: fieldRequiredErrorMessage },
							pattern: {
								value: new RegExp(selectedPostalCodeTemplate?.validationRegex ?? ""),
								message: format("settings.accountTab.professionalInformationSection.postalCode.errorMessage"),
							},
						}}
						render={({ field, fieldState }) => (
							<FormField
								title={format("settings.accountTab.professionalInformationSection.postalCode")}
								errorMessage={fieldState.error?.message}
							>
								<TextInput
									{...field}
									placeholder={selectedPostalCodeTemplate?.placeholder}
									error={fieldState.invalid}
									inputMode="text"
									autoCorrect="off"
									autoCapitalize="none"
								/>
							</FormField>
						)}
					/>

					<Controller
						name="cityOfPractice"
						control={control}
						rules={{
							required: { value: isNotNil(defaultValues.cityOfPractice), message: fieldRequiredErrorMessage },
						}}
						render={({ field, fieldState }) => (
							<FormField
								title={format("settings.accountTab.professionalInformationSection.cityOfPractice")}
								errorMessage={fieldState.error?.message}
							>
								<TextInput
									{...field}
									placeholder={format("settings.accountTab.professionalInformationSection.cityOfPractice")}
									error={fieldState.invalid}
									inputMode="text"
									autoCorrect="off"
									autoCapitalize="none"
								/>
							</FormField>
						)}
					/>
				</ControllersContainer>
			</Form>
		);
	}

	return (
		<BaseDialog
			closeOnEscape={false}
			header={format("settings.accountTab.editInfoDialog.header")}
			footer={
				<ButtonContainer>
					<Button
						loading={isLoading}
						type="submit"
						label={format("global.dialog.save")}
						onClick={handleSubmit(submitForm)}
					/>
				</ButtonContainer>
			}
			width={DIALOG_SIZE.S}
			{...otherProps}
		>
			{dialogContent}
		</BaseDialog>
	);
};

const Form = styled(motion.form)`
	${stack("M", "spacebetween", "stretch")};
	height: 100%;
`;

const ControllersContainer = styled.div`
	${stack("M", "flexstart", "stretch")};
`;

const ButtonContainer = styled.div`
	${row(24, "flexend")}
`;

const StyledNetworkError = styled(NetworkError)`
	margin-top: 8px;
`;
